import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * VariantsManager — fully inline admin/vendor UI to define option types
 * (e.g. Sabor, Tamaño), add chip-style values, preview combinations live,
 * and edit price/stock/image per variant. No prompt()/confirm() popups.
 */
export function VariantsManager({ productId, lang, toast }) {
  const [optionTypes, setOptionTypes] = useState([]);
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newOptionLabel, setNewOptionLabel] = useState("");
  const [pendingValue, setPendingValue] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(null);
  const fileRefs = useRef({});

  const load = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    const [{ data: ot }, { data: vs }] = await Promise.all([
      supabase.from("product_option_types").select("*").eq("product_id", productId).order("position"),
      supabase.from("product_variants").select("*").eq("product_id", productId).order("position"),
    ]);
    const types = (ot || []).map((t) => ({
      ...t,
      values: Array.from(new Set((vs || []).map((v) => v.attributes?.[t.name]).filter((x) => x != null && x !== ""))),
    }));
    setOptionTypes(types);
    setVariants(vs || []);
    setLoading(false);
  }, [productId]);

  useEffect(() => { load(); }, [load]);

  const addOptionType = async () => {
    const label = newOptionLabel.trim();
    if (!label) return;
    const name = label.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
    if (!name) { toast(`❌ ${lang === "es" ? "Nombre inválido" : "Invalid name"}`); return; }
    if (optionTypes.some((o) => o.name === name)) { toast(`❌ ${lang === "es" ? "Ya existe" : "Already exists"}`); return; }
    const { data, error } = await supabase.from("product_option_types").insert({
      product_id: productId, name, label, position: optionTypes.length,
    }).select().single();
    if (error) { toast(`❌ ${error.message}`); return; }
    setOptionTypes((p) => [...p, { ...data, values: [] }]);
    setNewOptionLabel("");
  };

  const removeOptionType = async (ot) => {
    const { error } = await supabase.from("product_option_types").delete().eq("id", ot.id);
    if (error) { toast(`❌ ${error.message}`); return; }
    setOptionTypes((p) => p.filter((x) => x.id !== ot.id));
    setConfirmDelete(null);
  };

  const commitValue = (otId) => {
    const raw = (pendingValue[otId] || "").trim();
    if (!raw) return;
    const parts = raw.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
    setOptionTypes((p) => p.map((o) =>
      o.id === otId ? { ...o, values: Array.from(new Set([...o.values, ...parts])) } : o
    ));
    setPendingValue((p) => ({ ...p, [otId]: "" }));
  };
  const removeValue = (otId, val) => {
    setOptionTypes((p) => p.map((o) => o.id === otId ? { ...o, values: o.values.filter((x) => x !== val) } : o));
  };

  const cartesian = useMemo(() => {
    const filled = optionTypes.filter((o) => o.values.length > 0);
    if (filled.length === 0) return [];
    let acc = [{}];
    for (const o of filled) {
      const next = [];
      for (const a of acc) for (const v of o.values) next.push({ ...a, [o.name]: v });
      acc = next;
    }
    return acc;
  }, [optionTypes]);

  const variantKey = (attrs) => optionTypes.map((o) => `${o.name}=${attrs?.[o.name] ?? ""}`).join("|");
  const existingByKey = useMemo(() => {
    const m = new Map();
    for (const v of variants) m.set(variantKey(v.attributes || {}), v);
    return m;
  }, [variants, optionTypes]);

  const buildName = (attrs) => optionTypes.filter((o) => attrs?.[o.name]).map((o) => attrs[o.name]).join(" / ");

  const generateMissing = async () => {
    if (cartesian.length === 0) { toast(`❌ ${lang === "es" ? "Agrega opciones y valores primero" : "Add options and values first"}`); return; }
    const missing = cartesian.filter((c) => !existingByKey.has(variantKey(c)));
    if (missing.length === 0) { toast(`✅ ${lang === "es" ? "Sin variantes faltantes" : "No missing variants"}`); return; }
    setSaving(true);
    const rows = missing.map((attrs, i) => ({
      product_id: productId,
      name: buildName(attrs) || "Default",
      attributes: attrs,
      price: 0,
      stock_quantity: 0,
      position: variants.length + i,
      is_active: true,
      is_default: variants.length === 0 && i === 0,
    }));
    const { error } = await supabase.from("product_variants").insert(rows);
    setSaving(false);
    if (error) { toast(`❌ ${error.message}`); return; }
    toast(`✅ ${missing.length} ${lang === "es" ? "variantes creadas" : "variants created"}`);
    load();
  };

  const updateField = (vid, patch) => setVariants((p) => p.map((v) => v.id === vid ? { ...v, ...patch } : v));
  const persist = async (v, patch) => {
    const { error } = await supabase.from("product_variants").update(patch).eq("id", v.id);
    if (error) toast(`❌ ${error.message}`);
  };
  const removeVariant = async (v) => {
    const { error } = await supabase.from("product_variants").delete().eq("id", v.id);
    if (error) { toast(`❌ ${error.message}`); return; }
    setVariants((p) => p.filter((x) => x.id !== v.id));
    setConfirmDelete(null);
  };
  const setDefault = async (v) => {
    const { error } = await supabase.from("product_variants").update({ is_default: false }).eq("product_id", productId).neq("id", v.id);
    if (error) { toast(`❌ ${error.message}`); return; }
    const { error: e2 } = await supabase.from("product_variants").update({ is_default: true }).eq("id", v.id);
    if (e2) { toast(`❌ ${e2.message}`); return; }
    setVariants((p) => p.map((x) => ({ ...x, is_default: x.id === v.id })));
  };

  const uploadImage = async (v, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast(`❌ ${lang === "es" ? "Solo imágenes" : "Images only"}`); return; }
    if (file.size > 10 * 1024 * 1024) { toast(`❌ ${lang === "es" ? "Máximo 10MB" : "Max 10MB"}`); return; }
    const path = `variants/${productId}/${v.id}-${Date.now()}.${file.name.split(".").pop() || "jpg"}`;
    const { error } = await supabase.storage.from("product-images").upload(path, file, { upsert: true, contentType: file.type });
    if (error) { toast(`❌ ${error.message}`); return; }
    const { data: pub } = supabase.storage.from("product-images").getPublicUrl(path);
    updateField(v.id, { image_url: pub.publicUrl });
    await persist(v, { image_url: pub.publicUrl });
    toast(`✅ ${lang === "es" ? "Imagen subida" : "Image uploaded"}`);
  };

  if (!productId) {
    return (
      <div style={{ fontSize: 11, color: "var(--tx3)", padding: 10 }}>
        {lang === "es" ? "Guarda el producto primero para añadir variantes." : "Save the product first to add variants."}
      </div>
    );
  }

  const suggestions = ["Sabor", "Tamaño", "Color", "Presentación"].filter(
    (s) => !optionTypes.some((o) => o.label.toLowerCase() === s.toLowerCase())
  );

  return (
    <div>
      {/* Step 1 */}
      <div style={{ marginBottom: 10, padding: 10, background: "var(--bg2)", borderRadius: 6, border: "1px solid var(--bd2)" }}>
        <div style={{ fontSize: 10, color: "var(--tx3)", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>
          {lang === "es" ? "Paso 1 · Define las opciones" : "Step 1 · Define options"}
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <input
            value={newOptionLabel}
            onChange={(e) => setNewOptionLabel(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addOptionType(); } }}
            placeholder={lang === "es" ? "Ej. Sabor, Tamaño, Color…" : "e.g. Flavor, Size, Color…"}
            style={{ ...inputCell, flex: 1, minWidth: 180, fontSize: 12, padding: "6px 8px" }} />
          <button type="button" className="btn bp bs" style={{ fontSize: 11 }} onClick={addOptionType} disabled={!newOptionLabel.trim()}>
            ＋ {lang === "es" ? "Agregar opción" : "Add option"}
          </button>
        </div>
        {suggestions.length > 0 && (
          <div style={{ marginTop: 8, display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ fontSize: 10, color: "var(--tx3)" }}>{lang === "es" ? "Sugerencias:" : "Suggestions:"}</span>
            {suggestions.map((s) => (
              <button key={s} type="button" onClick={() => setNewOptionLabel(s)}
                style={{ fontSize: 10, padding: "1px 6px", border: "1px solid var(--bd2)", borderRadius: 10, background: "var(--bg)", color: "var(--tx2)", cursor: "pointer" }}>
                + {s}
              </button>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <div style={{ fontSize: 11, color: "var(--tx3)" }}>{lang === "es" ? "Cargando…" : "Loading…"}</div>
      ) : optionTypes.length === 0 ? (
        <div style={{ fontSize: 11, color: "var(--tx3)", padding: 12, border: "1px dashed var(--bd2)", borderRadius: 6, textAlign: "center" }}>
          {lang === "es" ? "📦 Sin opciones aún. Añade una arriba para empezar." : "📦 No options yet. Add one above to start."}
        </div>
      ) : (
        <>
          <div style={{ fontSize: 10, color: "var(--tx3)", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>
            {lang === "es" ? "Paso 2 · Añade valores a cada opción" : "Step 2 · Add values per option"}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
            {optionTypes.map((ot) => (
              <div key={ot.id} style={{ padding: 10, border: "1px solid var(--bd2)", borderRadius: 6, background: "var(--bg2)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <b style={{ fontSize: 12 }}>
                    {ot.label} <span style={{ fontWeight: 400, color: "var(--tx3)", fontSize: 10 }}>({ot.values.length})</span>
                  </b>
                  <button type="button" className="btn bs" title={lang === "es" ? "Eliminar opción" : "Delete option"}
                    style={{ fontSize: 10, color: "var(--am)" }}
                    onClick={() => setConfirmDelete({ kind: "option", item: ot })}>🗑️</button>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 6 }}>
                  {ot.values.length === 0 ? (
                    <span style={{ fontSize: 10, color: "var(--tx3)" }}>{lang === "es" ? "Sin valores aún" : "No values yet"}</span>
                  ) : ot.values.map((v) => (
                    <span key={v} style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 8px", borderRadius: 12, background: "var(--bg)", border: "1px solid var(--bd2)", fontSize: 11 }}>
                      {v}
                      <button type="button" onClick={() => removeValue(ot.id, v)} style={{ border: 0, background: "transparent", color: "var(--am)", cursor: "pointer", padding: 0, fontSize: 13, lineHeight: 1 }}>×</button>
                    </span>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 4 }}>
                  <input
                    value={pendingValue[ot.id] || ""}
                    onChange={(e) => setPendingValue((p) => ({ ...p, [ot.id]: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === ",") { e.preventDefault(); commitValue(ot.id); }
                    }}
                    onBlur={() => commitValue(ot.id)}
                    placeholder={lang === "es" ? "Añadir valor (Enter o coma)" : "Add value (Enter or comma)"}
                    style={{ ...inputCell, flex: 1, fontSize: 11 }} />
                  <button type="button" className="btn bg bs" style={{ fontSize: 10 }} onClick={() => commitValue(ot.id)}>＋</button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {optionTypes.length > 0 && optionTypes.some((o) => o.values.length > 0) && (
        <div style={{ marginBottom: 10, padding: 8, background: "var(--bg)", border: "1px dashed var(--bd2)", borderRadius: 6 }}>
          <div style={{ fontSize: 10, color: "var(--tx3)", marginBottom: 6 }}>
            {lang === "es" ? "Vista previa:" : "Preview:"}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, maxHeight: 80, overflow: "auto" }}>
            {cartesian.slice(0, 30).map((c, i) => {
              const exists = existingByKey.has(variantKey(c));
              return (
                <span key={i} style={{
                  fontSize: 10, padding: "2px 6px", borderRadius: 4,
                  background: exists ? "var(--bg2)" : "color-mix(in srgb, var(--gr, #4ade80) 20%, transparent)",
                  border: `1px solid ${exists ? "var(--bd2)" : "var(--gr, #4ade80)"}`,
                  color: exists ? "var(--tx3)" : "var(--tx)",
                }}>
                  {buildName(c)} {exists ? "✓" : "✨"}
                </span>
              );
            })}
            {cartesian.length > 30 && <span style={{ fontSize: 10, color: "var(--tx3)" }}>+{cartesian.length - 30}…</span>}
          </div>
        </div>
      )}

      {optionTypes.length > 0 && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, paddingTop: 8, borderTop: "1px solid var(--bd2)" }}>
          <span style={{ fontSize: 11, color: "var(--tx3)" }}>
            <b style={{ color: "var(--tx)" }}>{cartesian.length}</b> {lang === "es" ? "combos · " : "combos · "}
            <b style={{ color: "var(--tx)" }}>{variants.length}</b> {lang === "es" ? "creadas" : "created"}
          </span>
          <button type="button" className="btn bp bs" style={{ fontSize: 11 }} disabled={saving} onClick={generateMissing}>
            {saving ? "…" : (lang === "es" ? "🧬 Generar variantes faltantes" : "🧬 Generate missing variants")}
          </button>
        </div>
      )}

      {variants.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <div style={{ fontSize: 10, color: "var(--tx3)", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>
            {lang === "es" ? "Paso 3 · Ajusta precio, stock e imagen" : "Step 3 · Adjust price, stock and image"}
          </div>
          <table className="tbl" style={{ fontSize: 11, minWidth: 600 }}>
            <thead>
              <tr>
                <th>{lang === "es" ? "Variante" : "Variant"}</th>
                <th>SKU</th>
                <th>{lang === "es" ? "Precio" : "Price"}</th>
                <th>Stock</th>
                <th>{lang === "es" ? "Imagen" : "Image"}</th>
                <th>{lang === "es" ? "Activa" : "Active"}</th>
                <th>{lang === "es" ? "Default" : "Default"}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {variants.map((v) => (
                <tr key={v.id}>
                  <td>
                    <b>{v.name}</b>
                    <div style={{ fontSize: 9, color: "var(--tx3)" }}>
                      {Object.entries(v.attributes || {}).map(([k, val]) => `${k}: ${val}`).join(" · ") || "—"}
                    </div>
                  </td>
                  <td>
                    <input value={v.sku || ""} onChange={(e) => updateField(v.id, { sku: e.target.value })}
                      onBlur={(e) => persist(v, { sku: e.target.value || null })}
                      style={{ ...inputCell, width: 90, fontFamily: "monospace" }} />
                  </td>
                  <td>
                    <input type="number" min={0} step="0.01" value={v.price ?? 0}
                      onChange={(e) => updateField(v.id, { price: e.target.value })}
                      onBlur={(e) => {
                        const n = parseFloat(e.target.value);
                        if (isNaN(n) || n < 0) { toast(`❌ ${lang === "es" ? "Precio inválido" : "Invalid price"}`); return; }
                        persist(v, { price: n });
                      }}
                      style={{ ...inputCell, width: 80 }} />
                  </td>
                  <td>
                    <input type="number" min={0} step="1" value={v.stock_quantity ?? 0}
                      onChange={(e) => updateField(v.id, { stock_quantity: e.target.value })}
                      onBlur={(e) => {
                        const n = parseInt(e.target.value, 10);
                        if (isNaN(n) || n < 0) { toast(`❌ ${lang === "es" ? "Stock inválido" : "Invalid stock"}`); return; }
                        persist(v, { stock_quantity: n });
                      }}
                      style={{ ...inputCell, width: 70 }} />
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                      {v.image_url
                        ? <img src={v.image_url} alt="" style={{ width: 28, height: 28, objectFit: "cover", borderRadius: 4 }} />
                        : <div style={{ width: 28, height: 28, borderRadius: 4, background: "var(--bg2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12 }}>📦</div>}
                      <input
                        ref={(el) => { fileRefs.current[v.id] = el; }}
                        type="file" accept="image/*" style={{ display: "none" }}
                        onChange={(e) => { uploadImage(v, e.target.files?.[0]); e.target.value = ""; }} />
                      <button type="button" className="btn bs" style={{ fontSize: 9 }} onClick={() => fileRefs.current[v.id]?.click()}>
                        {lang === "es" ? "Subir" : "Upload"}
                      </button>
                    </div>
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <input type="checkbox" checked={!!v.is_active}
                      onChange={(e) => { updateField(v.id, { is_active: e.target.checked }); persist(v, { is_active: e.target.checked }); }} />
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <input type="radio" name={`def-${productId}`} checked={!!v.is_default} onChange={() => setDefault(v)} />
                  </td>
                  <td>
                    <button type="button" className="btn bs" style={{ fontSize: 10, color: "var(--am)" }} onClick={() => setConfirmDelete({ kind: "variant", item: v })}>🗑️</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {confirmDelete && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1300 }} onClick={() => setConfirmDelete(null)}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--bg)", border: "1px solid var(--bd2)", borderRadius: 8, padding: 16, maxWidth: 320 }}>
            <div style={{ fontSize: 13, marginBottom: 12 }}>
              {confirmDelete.kind === "option"
                ? (lang === "es" ? `¿Eliminar opción "${confirmDelete.item.label}"? Las variantes existentes no se borran.` : `Delete option "${confirmDelete.item.label}"?`)
                : (lang === "es" ? `¿Eliminar variante "${confirmDelete.item.name}"?` : `Delete variant "${confirmDelete.item.name}"?`)}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 6 }}>
              <button type="button" className="btn bg bs" onClick={() => setConfirmDelete(null)} style={{ fontSize: 11 }}>
                {lang === "es" ? "Cancelar" : "Cancel"}
              </button>
              <button type="button" className="btn bs" style={{ fontSize: 11, background: "var(--am)", color: "#fff" }}
                onClick={() => confirmDelete.kind === "option" ? removeOptionType(confirmDelete.item) : removeVariant(confirmDelete.item)}>
                {lang === "es" ? "Eliminar" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inputCell = {
  fontSize: 11, padding: "4px 6px",
  border: "1px solid var(--bd2)", borderRadius: 4,
  background: "var(--bg2)", color: "var(--tx)", fontFamily: "var(--f)",
};